import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  Building2,
  ArrowLeft,
  TrendingUp,
  Calendar,
  FileText,
  BarChart3,
  DollarSign,
  Users,
  Globe,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  Layers,
  Upload,
  File,
  Trash2,
  FileUp,
  Sliders,
  Newspaper,
  BookOpen,
  AlertTriangle,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import Card, { CardHeader, CardBody } from '../components/ui/Card';
import LoadingCard from '../components/ui/LoadingCard';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Tooltip from '../components/ui/Tooltip';
import ReportDetailsModal from '../components/ReportDetailsModal';
import QuarterlyFilingsTable from '../components/QuarterlyFilingsTable';
import FinancialTimeline from '../components/visualizations/FinancialTimeline';
import BusinessModelCanvas from '../components/visualizations/BusinessModelCanvas';
import { standaloneCompaniesAPI, indiaCompanyAPI, ukCompanyAPI } from '../lib/api';
import { BusinessModelCanvas as BusinessModelCanvasType } from '../types';
import FinancialDashboardTab from '../components/financial/FinancialDashboardTab';
import DataQualityPanel from '../components/financial/DataQualityPanel';
import DataQualityConsole from '../components/financial/dataQuality/DataQualityConsole';
import { useValidationSummary } from '../components/financial/hooks/useDataQuality';
import { useCompanyFinancials } from '../components/financial/hooks/useCompanyFinancials';
import {
  useCompanyCompetitors,
  useCompetitorFinancials,
} from '../components/financial/hooks/useCompetitors';
import ManageCompetitorsModal from '../components/financial/ManageCompetitorsModal';
import { useFinancialStore } from '../stores/financialStore';
import MetricCardsRow from '../components/financial/sections/MetricCardsRow';
import { hasProfitLoss, hasBalanceSheet } from '../components/financial/utils/dataCompleteness';
import { isPdfCompany } from '../components/financial/utils/financeSource';
import FinancialNoData from '../components/financial/FinancialNoData';
import { formatCurrencyValue, formatFiscalYear } from '../components/financial/utils/formatters';
import SECFilingsTab from '../components/sec/SECFilingsTab';
import CompanyFilingsTab from '../components/financial/CompanyFilingsTab';
import { NewsIntelligenceTab } from '../components/company-research/news-intelligence';
import { DocumentInsightsTab } from '../components/company-research/document-insights';

type TabType = 'overview' | 'financials' | 'revenue' | 'ev' | 'finance' | 'sensitivity' | 'businessModel' | 'research' | 'reports' | 'filings' | 'news' | 'docInsights' | 'competitors' | 'dataQuality';

// Helper function to format large numbers to readable format
const formatCurrency = (value: string | number | undefined | null, region?: any): string => {
  if (!value) return 'N/A';

  // If already a formatted string, return as-is
  if (typeof value === 'string') {
    return value;
  }

  return formatCurrencyValue(value, region);
};

interface Company {
  companyId: string;
  companyName: string;  // Changed from 'name' to match API
  ticker?: string;
  cik?: string;
  source: string;
  industry?: string;
  sector?: string;
  website?: string;
  headquarters?: string;
  employeeCount?: number;
  reportCount: number;
  lastResearchedAt: string;
  dateRange?: {
    from: string;
    to: string;
  };
  latestMetrics?: {
    revenue: string | number;
    netIncome: string | number;
    grossProfit: string | number;
    operatingIncome: string | number;
    period: string;
  };
  executiveSummary?: string;
  latestRevenue?: string;
  latestPeriod?: string;
}

interface SegmentMetrics {
  metricsId: string;
  revenue?: number;
  operatingIncome?: number;
  grossMargin?: number;
  operatingMargin?: number;
  revenuePercentage?: number;
  yoyGrowth?: number;
  qoqGrowth?: number;
}

interface BusinessSegment {
  segmentId: string;
  segmentName: string;
  segmentType?: string;
  description?: string;
  currentMetrics?: SegmentMetrics;
  metrics?: {
    revenue?: string;  // API returns formatted string like "$65.0B"
    operatingIncome?: string;
    grossMargin?: string;
    operatingMargin?: string;
  };
}

interface Report {
  reportId: string;
  type: string;
  period: string;
  filingDate: string;
  url?: string;
  metrics: {
    revenue?: string;
    netIncome?: string;
    grossProfit?: string;
    operatingIncome?: string;
    totalAssets?: string;
    totalLiabilities?: string;
    shareholdersEquity?: string;
  };
  segments?: BusinessSegment[];
  // Extraction status indicators
  xbrlExtracted?: boolean;
  aiExtracted?: boolean;
  segmentsExtracted?: boolean;
  status?: string;  // Overall processing status ("Processed", "Partially Processed", "Not Processed")
  valuation?: {
    enterpriseValue?: string;
    ebitda?: string;
    evEbitdaRatio?: string;
    components: {
      marketCap?: string;
      totalDebt?: string;
      cashAndEquivalents?: string;
      minorityInterest?: string;
      operatingIncome?: string;
      depreciationAmortization?: string;
    };
    calculationStatus: {
      evCalculated: boolean;
      ebitdaCalculated: boolean;
      marketCapSource: string;
      evNotes: string;
      ebitdaNotes: string;
    };
  };
  balanceSheet?: {
    // Assets
    totalAssets?: string | null;
    currentAssets?: string | null;
    cash?: string | null;
    marketableSecurities?: string | null;
    accountsReceivable?: string | null;
    inventory?: string | null;
    propertyPlantEquipment?: string | null;
    intangibleAssets?: string | null;
    longTermInvestments?: string | null;
    // Liabilities
    totalLiabilities?: string | null;
    currentLiabilities?: string | null;
    accountsPayable?: string | null;
    shortTermDebt?: string | null;
    currentPortionLongTermDebt?: string | null;
    longTermDebt?: string | null;
    deferredRevenue?: string | null;
    pensionLiabilities?: string | null;
    // Equity
    shareholdersEquity?: string | null;
    commonStock?: string | null;
    retainedEarnings?: string | null;
    treasuryStock?: string | null;
    // Working Capital
    workingCapital?: string | null;
    currentRatio?: number | null;
    quickRatio?: number | null;
  };
  incomeStatement?: {
    revenue?: string | null;
    costOfRevenue?: string | null;
    grossProfit?: string | null;
    researchDevelopment?: string | null;
    salesMarketing?: string | null;
    generalAdministrative?: string | null;
    totalOperatingExpenses?: string | null;
    depreciationAmortization?: string | null;
    operatingIncome?: string | null;
    netIncome?: string | null;
    period?: string;
  };
  cashFlow?: {
    // Operating Activities
    netCashFromOperations?: string | null;
    netIncome?: string | null;
    depreciationAmortization?: string | null;
    stockBasedCompensation?: string | null;
    deferredTaxes?: string | null;
    workingCapitalChanges?: string | null;
    // Investing Activities
    netCashFromInvesting?: string | null;
    capitalExpenditures?: string | null;
    acquisitions?: string | null;
    investmentPurchases?: string | null;
    investmentSales?: string | null;
    // Financing Activities
    netCashFromFinancing?: string | null;
    debtIssuance?: string | null;
    debtRepayment?: string | null;
    equityIssuance?: string | null;
    stockRepurchases?: string | null;
    dividendsPaid?: string | null;
    // Summary
    netChangeInCash?: string | null;
    cashBeginningOfPeriod?: string | null;
    cashEndOfPeriod?: string | null;
    freeCashFlow?: string | null;
  };
  ratios?: {
    // Profitability
    grossMargin?: number | null;
    operatingMargin?: number | null;
    netMargin?: number | null;
    returnOnAssets?: number | null;
    returnOnEquity?: number | null;
    returnOnInvestedCapital?: number | null;
    // Liquidity
    currentRatio?: number | null;
    quickRatio?: number | null;
    cashRatio?: number | null;
    // Leverage
    debtToEquity?: number | null;
    debtToAssets?: number | null;
    interestCoverage?: number | null;
    // Efficiency
    assetTurnover?: number | null;
    inventoryTurnover?: number | null;
    receivablesTurnover?: number | null;
    // Valuation
    priceToEarnings?: number | null;
    priceToBook?: number | null;
    priceToSales?: number | null;
    evToEbitda?: number | null;
  };
}

// ─── Financial summary helpers ───────────────────────────────────────────────

function generateExecutiveSummary(
  companyName: string,
  latest: any,
  sector: string | undefined,
  region: any,
  allData: any[],
): string {
  const revenue = latest.total_revenue ?? latest.revenue_from_operations;
  const unit = region?.currency?.unit_abbr ?? 'Cr';
  const year = latest.fiscal_year_label ?? `FY${String(latest.year).slice(-2)}`;

  let s = `**${companyName}** is ${sector ? `a ${sector} company` : 'an India-listed company'} tracked through the financial data pipeline.\n\n`;

  if (revenue != null) {
    s += `In **${year}**, the company reported revenues of **${formatCurrencyValue(revenue, region)} ${unit}**`;
    if (latest.ebitda_margin != null) {
      s += ` with an EBITDA margin of **${(latest.ebitda_margin * 100).toFixed(1)}%**`;
    }
    s += '.\n\n';
  }

  if (latest.roic != null && latest.wacc != null) {
    const spread = (latest.roic - latest.wacc) * 100;
    s += `**ROIC of ${(latest.roic * 100).toFixed(1)}%** vs WACC of **${(latest.wacc * 100).toFixed(1)}%** — `;
    s += spread >= 0
      ? `a positive spread of **+${spread.toFixed(1)}pp**, confirming the business is creating economic value above its cost of capital.\n\n`
      : `a negative spread of **${spread.toFixed(1)}pp**, indicating value erosion that merits attention.\n\n`;
  }

  if (latest.enterprise_value != null) {
    s += `Enterprise value stands at **${formatCurrencyValue(latest.enterprise_value, region)} ${unit}**`;
    if (latest.ev_ebitda != null) s += ` (EV/EBITDA: **${latest.ev_ebitda.toFixed(1)}x**)`;
    s += '.\n\n';
  }

  if (allData.length >= 2) {
    const first = allData[0];
    const firstRev = first.total_revenue ?? first.revenue_from_operations;
    if (firstRev != null && revenue != null && firstRev > 0) {
      const cagr = (Math.pow(revenue / firstRev, 1 / (allData.length - 1)) - 1) * 100;
      const firstLabel = first.fiscal_year_label ?? `FY${String(first.year).slice(-2)}`;
      s += `Revenue has compounded at a **${cagr.toFixed(1)}% CAGR** from ${firstLabel} to ${year}.\n\n`;
    }
  }

  return s.trim();
}

type InsightItem = {
  insight_id: string;
  title: string;
  description: string;
  sentiment: string;
  action: string;
  confidence_score: number;
};

function generateIndiaInsights(latest: any, region: any): InsightItem[] {
  const items: InsightItem[] = [];
  const unit = region?.currency?.unit_abbr ?? 'Cr';

  if (latest.ebitda_margin != null) {
    const m = (latest.ebitda_margin * 100).toFixed(1);
    const healthy = latest.ebitda_margin >= 0.15;
    items.push({
      insight_id: 'ebitda_margin',
      title: `EBITDA Margin: ${m}%`,
      description: healthy
        ? `A healthy EBITDA margin of ${m}% reflects strong operational efficiency and pricing power.`
        : `EBITDA margin of ${m}% trails the 15% benchmark — reviewing cost structure and pricing is recommended.`,
      sentiment: healthy ? 'positive' : 'negative',
      action: healthy ? 'celebrate' : 'investigate',
      confidence_score: 0.9,
    });
  }

  if (latest.roic != null && latest.wacc != null) {
    const spread = (latest.roic - latest.wacc) * 100;
    items.push({
      insight_id: 'roic_wacc',
      title: `ROIC vs WACC: ${spread >= 0 ? '+' : ''}${spread.toFixed(1)}pp spread`,
      description: spread >= 0
        ? `ROIC of ${(latest.roic * 100).toFixed(1)}% exceeds WACC of ${(latest.wacc * 100).toFixed(1)}% — the company generates returns above its cost of capital.`
        : `ROIC of ${(latest.roic * 100).toFixed(1)}% trails WACC of ${(latest.wacc * 100).toFixed(1)}% — capital efficiency improvement is needed.`,
      sentiment: spread >= 0 ? 'positive' : 'negative',
      action: spread >= 0 ? 'celebrate' : 'address',
      confidence_score: 0.95,
    });
  }

  if (latest.ev_ebitda != null) {
    const ev = latest.ev_ebitda.toFixed(1);
    items.push({
      insight_id: 'ev_ebitda',
      title: `Valuation: ${ev}x EV/EBITDA`,
      description: latest.ev_ebitda > 20
        ? `Trading at ${ev}x EV/EBITDA — the market is pricing in premium growth expectations.`
        : `EV/EBITDA of ${ev}x represents a reasonable valuation relative to earnings power.`,
      sentiment: 'neutral',
      action: 'monitor',
      confidence_score: 0.85,
    });
  }

  if (latest.ebitda != null && latest.ebitda > 0) {
    const netDebt = latest.net_debt ?? ((latest.total_debt ?? 0) - (latest.cash_and_equivalents ?? 0));
    if (netDebt != null) {
      const lev = netDebt / latest.ebitda;
      const high = lev > 3;
      items.push({
        insight_id: 'leverage',
        title: `Leverage: ${lev.toFixed(1)}x Net Debt / EBITDA`,
        description: high
          ? `Net Debt/EBITDA of ${lev.toFixed(1)}x is elevated — debt servicing may constrain future investments.`
          : `Net Debt/EBITDA of ${lev.toFixed(1)}x is well-managed, reflecting a healthy balance sheet.`,
        sentiment: high ? 'negative' : 'positive',
        action: high ? 'investigate' : 'celebrate',
        confidence_score: 0.85,
      });
    }
  }

  const revenue = latest.total_revenue ?? latest.revenue_from_operations;
  if (revenue != null) {
    items.push({
      insight_id: 'revenue',
      title: `Revenue: ${formatCurrencyValue(revenue, region)} ${unit}`,
      description: `Latest reported annual revenue stands at ${formatCurrencyValue(revenue, region)} ${unit}.`,
      sentiment: 'neutral',
      action: 'monitor',
      confidence_score: 1.0,
    });
  }

  return items;
}

// ─────────────────────────────────────────────────────────────────────────────

interface CompetitorCompareToggleProps {
  enabled: boolean;
  onToggle: (b: boolean) => void;
  competitorsList: string[];
  competitorsListLoading: boolean;
  competitorsListError: Error | null;
  financialsLoading: boolean;
  failedCompetitors: string[];
  successCount: number;
  onRetry: () => void;
  onManage: () => void;
}

function CompetitorCompareToggle({
  enabled,
  onToggle,
  competitorsList,
  competitorsListLoading,
  competitorsListError,
  financialsLoading,
  failedCompetitors,
  successCount,
  onRetry,
  onManage,
}: CompetitorCompareToggleProps) {
  const hasNone =
    !competitorsListLoading && !competitorsListError && competitorsList.length === 0;
  // Disable only while actively loading; let the user click in the error/empty
  // case so they can read the inline message and trigger Retry.
  const disabled = competitorsListLoading;

  let status: { kind: 'loading' | 'error' | 'empty' | 'ready'; text: string } = {
    kind: 'ready',
    text: `${competitorsList.length} peer${competitorsList.length === 1 ? '' : 's'} ready`,
  };
  if (competitorsListLoading) {
    status = { kind: 'loading', text: 'Identifying competitors…' };
  } else if (competitorsListError) {
    status = {
      kind: 'error',
      text: `Failed to load competitors: ${competitorsListError.message}`,
    };
  } else if (hasNone) {
    status = {
      kind: 'empty',
      text: 'No competitors identified for this company yet.',
    };
  }

  const statusColor =
    status.kind === 'error'
      ? 'text-red-700'
      : status.kind === 'empty'
        ? 'text-amber-700'
        : status.kind === 'loading'
          ? 'text-neutral-500'
          : 'text-emerald-700';

  return (
    <div className="mb-5 rounded-lg border border-neutral-200 bg-neutral-50/60 px-4 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Users className="h-4 w-4 text-neutral-500" />
          <div>
            <div className="text-sm font-semibold text-neutral-800">
              Compare with competitors
            </div>
            <div className="text-xs text-neutral-500">
              Reveals a Competitor Analysis tab with peer comparisons. Other tabs stay focal-company only.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {enabled && !financialsLoading && failedCompetitors.length > 0 && (
            <span className="text-xs text-amber-700">
              Showing {successCount} of {competitorsList.length} — {failedCompetitors.join(', ')} unavailable
            </span>
          )}
          {(status.kind === 'error' || status.kind === 'empty') && (
            <button
              type="button"
              onClick={onRetry}
              className="text-xs font-medium text-primary-700 hover:text-primary-800 inline-flex items-center gap-1"
            >
              <RefreshCw className="h-3 w-3" />
              Retry
            </button>
          )}
          <button
            type="button"
            onClick={onManage}
            className="inline-flex items-center gap-1.5 rounded-md border border-neutral-300 bg-white px-2.5 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50 hover:border-neutral-400"
          >
            <Users className="h-3 w-3" />
            Manage Competitors
            {competitorsList.length > 0 && (
              <span className="inline-flex items-center justify-center min-w-[1.25rem] h-4 rounded-full bg-primary-100 text-primary-700 text-[10px] font-semibold px-1">
                {competitorsList.length}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => !disabled && onToggle(!enabled)}
            disabled={disabled}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
              enabled ? 'bg-primary-600' : 'bg-neutral-300'
            }`}
            aria-pressed={enabled}
            aria-label="Compare with competitors"
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                enabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>
      <div className={`mt-1.5 text-xs ${statusColor} inline-flex items-center gap-1.5`}>
        {status.kind === 'loading' && <Loader2 className="h-3 w-3 animate-spin" />}
        {status.text}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function CompanyDetail() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [company, setCompany] = useState<Company | null>(null);
  // Hoisted here (rather than inside DataQualityPanel alone) so the Data
  // Quality TAB can be offered — and its severity dot lit — before the reader
  // ever opens the console. Uses the SAME id and `includePrevious` as the
  // panel's own call below, so React Query serves both from one request
  // instead of firing the fetch twice.
  const validationSummary = useValidationSummary(company?.companyId, true);
  const validationChains = validationSummary.data?.chains ?? [];
  // Measured height of the sticky tab bar, published as a CSS variable so
  // sticky content inside a tab can offset itself correctly. The bar wraps to
  // two rows at narrow widths, so this cannot be a constant.
  const tabBarRef = useRef<HTMLDivElement>(null);
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);

  // Backend-driven UI config — determines tabs, features, and region
  const [uiConfig, setUiConfig] = useState<{
    country: string;
    dataSource: string;
    availableTabs: string[];
    features: Record<string, boolean>;
    regionConfig: any;
  } | null>(null);

  // Country & feature detection — driven by backend ui-config (which reads
  // country directly from the Neo4j Company node), with ID-prefix and CIK
  // heuristics as fallback for the brief window before uiConfig loads.
  const companyCountry =
    uiConfig?.country ||
    (company?.companyId?.startsWith('uk_') || company?.companyId?.startsWith('uk-')
      ? 'UK'
      : company?.companyId?.startsWith('ind-') || company?.companyId?.startsWith('ind_')
        ? 'INDIA'
        : // comp_us_* = US PDF extraction (no CIK, source isn't "sec")
          company?.companyId?.startsWith('comp_us_') ||
            !!company?.cik ||
            company?.source === 'sec' ||
            company?.source === 'sec_edgar'
          ? 'US'
          : 'INDIA');
  const isUSCompany = companyCountry === 'US';
  const isNonUSCompany = companyCountry === 'INDIA' || companyCountry === 'UK';

  // PDF-ingested vs web-scraped. Drives the header source chip and disables
  // the scrape-refresh action (there is no web source to re-scrape — a newer
  // PDF has to be uploaded instead). A company id like "ind_pdf_xxxx", a
  // source of "pdf_extract", or any "pdfrep_*" report marks it PDF-sourced.
  const isPdfSourced = isPdfCompany(company?.companyId, company?.source, reports);
  const hasSecReports = uiConfig?.features?.hasSecFilings ?? (!!company?.cik && reports.length > 0);
  const hasFilingsList = uiConfig?.features?.hasFilingsList ?? (isNonUSCompany && reports.length > 0);

  // Fetch financial data for all companies to populate quick stats & dashboard tabs
  const { data: financialData } = useCompanyFinancials(
    company?.companyName ?? null,
    10,
    isUSCompany ? 'US' : undefined
  );
  const latestFinancials = financialData?.latest_data;
  const regionConfig = financialData?.region;

  // The latest reporting year must have BOTH P&L and Balance Sheet for the
  // computed-analysis surfaces (KPI strip, executive summary, key insights) to
  // be meaningful — otherwise BS-derived cards (EV, ROIC, Net Debt) show wrong
  // zeros. Mirrors the FinancialDashboardTab gate so the whole page stays
  // consistent. Raw filings (P&L present) are intentionally NOT gated by this.
  const latestFinancialsComplete = !!latestFinancials
    && hasProfitLoss([latestFinancials])
    && hasBalanceSheet([latestFinancials]);

  // Competitor comparison — toggle drives fetch fan-out.
  const compareWithCompetitors = useFinancialStore((s) => s.compareWithCompetitors);
  const setCompareWithCompetitors = useFinancialStore((s) => s.setCompareWithCompetitors);
  const competitorsList = useFinancialStore((s) => s.competitorsList);
  const competitorsLoading = useFinancialStore((s) => s.competitorsLoading);
  const resetCompetitorState = useFinancialStore((s) => s.resetCompetitorState);

  const competitorsQuery = useCompanyCompetitors(company?.companyName ?? null, 20);
  const competitorFinancials = useCompetitorFinancials(
    competitorsList,
    compareWithCompetitors && competitorsList.length > 0,
    10
  );
  const [manageCompetitorsOpen, setManageCompetitorsOpen] = useState(false);

  // Reset competitor state whenever the company changes so peers from the
  // previous page do not leak across.
  useEffect(() => {
    resetCompetitorState();
  }, [company?.companyName, resetCompetitorState]);

  // The Competitors tab exists only while the toggle is on. If the user turns
  // the toggle off while viewing it, fall back to Overview so we never render a
  // tab that has been removed from the bar.
  useEffect(() => {
    if (!compareWithCompetitors && activeTab === 'competitors') {
      setActiveTab('overview');
    }
  }, [compareWithCompetitors, activeTab]);

  const [error, setError] = useState<string | null>(null);
  const [insights, setInsights] = useState<any[]>([]);
  const [companyAnalysisData, setCompanyAnalysisData] = useState<any | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [businessModel, setBusinessModel] = useState<BusinessModelCanvasType | null>(null);
  const [businessModelLoading, setBusinessModelLoading] = useState(false);
  const [businessModelError, setBusinessModelError] = useState<string | null>(null);

  // Modal state for report details
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);

  // Refresh / delete actions in the header
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Research files state
  const [researchFiles, setResearchFiles] = useState<Array<{
    id: string;
    name: string;
    size: number;
    uploadedAt: Date;
    type: string;
  }>>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Guard refs — prevent duplicate calls caused by React 18 StrictMode double-invoking effects
  const fetchCompanyDataCalledRef = useRef(false);
  const insightsFetchedForRef = useRef<string | null>(null);

  // Fetch AI-generated insights via POST /analysis/companyInsights
  const fetchInsights = async (companyName?: string) => {
    const name = companyName;
    if (!name) return;
    // Skip if already fetched for this company name in this mount cycle
    if (insightsFetchedForRef.current === name) return;
    insightsFetchedForRef.current = name;

    try {
      setInsightsLoading(true);
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/analysis/companyInsights`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_name: name, years: 10 }),
      });
      if (response.ok) {
        const data = await response.json();
        setCompanyAnalysisData(data);
      }
    } catch (err) {
      console.error('Error fetching insights:', err);
    } finally {
      setInsightsLoading(false);
    }
  };

  // Fetch business model data
  const fetchBusinessModel = async () => {
    if (!companyId) return;

    try {
      setBusinessModelLoading(true);
      setBusinessModelError(null);
      const data = await standaloneCompaniesAPI.getBusinessModel(companyId);
      setBusinessModel(data);
    } catch (err: any) {
      console.error('Error fetching business model:', err);
      setBusinessModelError(
        err.response?.data?.detail ||
        'Failed to load business model. This feature requires SEC filing data to be processed.'
      );
      setBusinessModel(null);
    } finally {
      setBusinessModelLoading(false);
    }
  };

  // Refresh business model using RAG extraction
  const refreshBusinessModel = async () => {
    if (!companyId) return;

    try {
      setBusinessModelLoading(true);
      setBusinessModelError(null);
      console.log('Refreshing business model for:', companyId);

      // Call POST refresh endpoint to trigger RAG extraction
      await standaloneCompaniesAPI.refreshBusinessModel(companyId);

      // After successful refresh, fetch the newly generated data
      const data = await standaloneCompaniesAPI.getBusinessModel(companyId);
      setBusinessModel(data);
    } catch (err: any) {
      console.error('Error refreshing business model:', err);
      setBusinessModelError(
        err.response?.data?.detail ||
        'Failed to generate business model. Please try again.'
      );
      setBusinessModel(null);
    } finally {
      setBusinessModelLoading(false);
    }
  };

  // Regenerate insights for latest report
  const regenerateInsights = async () => {
    if (!companyId || reports.length === 0) {
      console.error('Cannot regenerate: missing companyId or reports');
      return;
    }

    try {
      setInsightsLoading(true);
      const latestReportId = reports[0].reportId;
      console.log('Regenerating insights for:', { companyId, latestReportId });

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/companies/companies/${companyId}/reports/${latestReportId}/insights/regenerate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Insights generated:', data);
        // Refresh insights after regeneration
        await fetchInsights(company?.companyName ?? undefined);
      } else {
        const errorData = await response.json();
        console.error('Failed to regenerate insights:', response.status, errorData);
        alert(`Failed to regenerate insights: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error regenerating insights:', err);
      alert(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setInsightsLoading(false);
    }
  };

  // Fetch company data and reports from API
  const fetchCompanyData = async () => {
    if (!companyId) return;

    try {
      setLoading(true);

      // Fetch company details
      const companyResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/companies/${companyId}`);
      if (!companyResponse.ok) {
        throw new Error(`Failed to fetch company: ${companyResponse.statusText}`);
      }
      const companyData = await companyResponse.json();
      setCompany(companyData);

      // Fetch UI config from backend (determines tabs, features, region)
      try {
        const configResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/companies/${companyId}/ui-config`);
        if (configResponse.ok) {
          const config = await configResponse.json();
          setUiConfig(config);
        }
      } catch (configErr) {
        console.warn('Failed to fetch UI config, using fallback logic:', configErr);
      }

      // Fetch reports if company has any
      if (companyData.reportCount > 0) {
        const reportsResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/companies/${companyId}/reports`);
        if (reportsResponse.ok) {
          const reportsData = await reportsResponse.json();
          // API returns array directly, not wrapped in an object
          setReports(Array.isArray(reportsData) ? reportsData : []);
        }
      }

      // Fetch AI insights via companyInsights API
      fetchInsights(companyData.companyName);

      setError(null);
    } catch (err) {
      console.error('Error fetching company:', err);
      setError(err instanceof Error ? err.message : 'Failed to load company details');
      setCompany(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Guard against React 18 StrictMode double-invocation
    if (fetchCompanyDataCalledRef.current) return;
    fetchCompanyDataCalledRef.current = true;
    fetchCompanyData();
    return () => {
      // Reset on unmount so navigating back to same companyId re-fetches
      fetchCompanyDataCalledRef.current = false;
      insightsFetchedForRef.current = null;
    };
  }, [companyId]);

  // Fetch business model when tab is active
  useEffect(() => {
    if (activeTab === 'businessModel' && companyId && !businessModel && !businessModelLoading) {
      fetchBusinessModel();
    }
  }, [activeTab, companyId]);

  // Publish the tab bar's height on the document root so sticky content in any
  // tab can sit directly beneath it. Re-measured on resize because the nav
  // wraps: at laptop widths it is two rows tall, not one.
  useEffect(() => {
    const node = tabBarRef.current;
    if (!node) return;

    const publish = () => {
      document.documentElement.style.setProperty(
        '--dq-sticky-top',
        `${Math.round(node.getBoundingClientRect().height)}px`
      );
    };

    publish();
    // Observing the node covers every cause of a height change — a viewport
    // resize, and a tab appearing once its data loads — so no dependency on
    // the tab list is needed (and `tabs` is not in scope yet here).
    const observer = new ResizeObserver(publish);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // All companies get the financial dashboard tabs (Finance, EV, Revenue, Sensitivity).
  // Companies with SEC XBRL data (hasSecReports) additionally get SEC-specific tabs.
  const baseTabs = [
    { id: 'overview', label: 'Overview', icon: Building2 },
    { id: 'finance', label: 'Finance', icon: DollarSign },
    { id: 'ev', label: 'EV Insights', icon: BarChart3 },
    { id: 'revenue', label: 'Revenue & Capital', icon: TrendingUp },
    // Competitors tab is revealed by the "Compare with competitors" toggle; it
    // holds every peer comparison, keeping the other tabs focal-company only.
    ...(compareWithCompetitors ? [{ id: 'competitors', label: 'Competitor Analysis', icon: Users }] : []),
    { id: 'sensitivity', label: 'Sensitivity Analysis', icon: Sliders },
  ];
  const hasBusinessModel = uiConfig?.features?.hasBusinessModel ?? (hasSecReports && !!company?.cik);
  const hasQuarterlyReports = !!company?.cik;  // Reports tab requires CIK for SEC EDGAR fetching
  // Label depends on data source: SEC EDGAR companies use "SEC Filings", MongoDB-scraped use "Financials"
  const financialsTabLabel = (uiConfig?.dataSource === 'neo4j_sec' || !!company?.cik) ? 'SEC Filings' : 'Financials';
  const secTabs = hasSecReports
    ? [
        { id: 'financials', label: financialsTabLabel, icon: FileText },
        ...(hasBusinessModel ? [{ id: 'businessModel', label: 'Business Model', icon: Layers }] : []),
        ...(hasQuarterlyReports ? [{ id: 'reports', label: 'Reports', icon: FileText }] : []),
      ]
    : [];
  // India / UK companies get a single merged "Filings & Reports" tab in
  // place of SEC Filings + Reports + Business Model. Sourced from Neo4j
  // FinancialReport nodes — same data the rest of the app already uses.
  const filingsTabs = isNonUSCompany && hasFilingsList
    ? [{ id: 'filings', label: 'Filings & Reports', icon: FileText }]
    : [];
  const tabs = [
    ...baseTabs,
    ...filingsTabs,
    ...secTabs,
    { id: 'news', label: 'Market Analysis', icon: Newspaper },
    ...(companyCountry === 'INDIA' ? [{ id: 'docInsights', label: 'Document Insights', icon: BookOpen }] : []),
    // Only offered once something has actually been validated — a 404 means
    // nothing is stored, and an empty tab would invite a click that leads
    // nowhere.
    ...(validationChains.length > 0
      ? [{ id: 'dataQuality', label: 'Data Quality', icon: ShieldCheck }]
      : []),
  ];
  const dataQualityNeedsAttention = validationChains.some(
    (chain) => chain.summary?.status !== 'clean'
  );

  const handleRefresh = async () => {
    if (!company || isRegenerating) return;
    setIsRegenerating(true);
    toast.info(`Refreshing financial data for ${company.companyName}…`);
    try {
      if (companyCountry === 'US') {
        const res = await standaloneCompaniesAPI.fetchUSFinancialData({
          company_name: company.companyName,
          company_code: company.ticker,
          time_range: '5years',
        });
        const status = String((res as any)?.status || 'success').toLowerCase();
        if (status === 'error') {
          throw new Error((res as any)?.message || 'US financial data refresh failed');
        }
      } else if (companyCountry === 'UK') {
        const res = await ukCompanyAPI.triggerResearch({
          company_name: company.companyName,
          company_code: company.cik,
          data_type: 'annual',
          time_range: '5years',
        });
        if (res?.status === 'error') {
          throw new Error(res?.message || 'UK financial data refresh failed');
        }
      } else {
        const res = await indiaCompanyAPI.triggerResearch({
          company_name: company.companyName,
          data_type: 'consolidated',
        });
        const triggerStatus = (res?.status || '').toLowerCase();
        if (['failed', 'error', 'not_found', 'failure'].includes(triggerStatus) || (res as any)?.error) {
          throw new Error((res as any)?.error || res?.message || 'India financial data refresh failed');
        }
      }
      toast.success(`Financial data refreshed for ${company.companyName}`);
      // Drop every cached financial query (company financials, summaries, metric
      // cards, dashboards, trends, EV insights, competitors — all keyed under
      // 'financial') so the tabs refetch the freshly-ingested figures. Without
      // this, useCompanyFinancials' 30-min staleTime kept serving the old data
      // from cache and the refresh appeared to do nothing.
      await queryClient.invalidateQueries({ queryKey: ['financial'] });
      // Re-pull company + reports so the page shows the new data.
      fetchCompanyDataCalledRef.current = false;
      await fetchCompanyData();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Refresh failed';
      console.error('Failed to refresh company:', err);
      toast.error(msg);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!company || isDeleting) return;
    setShowDeleteConfirm(false);
    setIsDeleting(true);
    try {
      await standaloneCompaniesAPI.deleteCompany(company.companyId);
      toast.success(`${company.companyName} deleted`);
      navigate('/companies');
    } catch (err) {
      console.error('Failed to delete company:', err);
      toast.error(err instanceof Error ? err.message : 'Delete failed');
      setIsDeleting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="p-6 max-w-screen-2xl mx-auto">
        <LoadingCard
          title="Loading company profile"
          message="Fetching company details, financials and reports…"
          height={320}
        />
      </div>
    );
  }

  // Error state
  if (error || !company) {
    return (
      <div className="p-6 max-w-screen-2xl mx-auto">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/companies')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Companies
        </Button>
        <Card>
          <CardBody className="text-center py-12">
            <div className="text-error-600 mb-4">⚠️</div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
              Failed to Load Company
            </h3>
            <p className="text-neutral-600 mb-6">
              {error || 'Company not found'}
            </p>
            <Button variant="primary" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-screen-2xl mx-auto">
      {/* Back nav */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/companies')}
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Companies
      </Button>

      {/* Header Card */}
      <div className="bg-white border border-neutral-200 rounded-2xl shadow-sm p-6 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-5">
            {/* Gradient initials avatar */}
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shrink-0 shadow-md">
              <span className="text-white text-xl font-bold select-none">
                {company.companyName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold text-neutral-900">
                  {company.companyName}
                </h1>
                {company.ticker && (
                  <span className="px-2.5 py-0.5 rounded-full bg-primary-50 border border-primary-200 text-primary-700 text-sm font-semibold">
                    {company.ticker}
                  </span>
                )}
                {/* Data provenance: extracted from an uploaded annual-report
                    PDF vs scraped from web sources. Both share the same graph
                    shape and calculation engine — only the origin differs. */}
                <Tooltip
                  content={
                    isPdfSourced
                      ? 'Financial data extracted from an uploaded annual-report PDF'
                      : 'Financial data ingested from web sources (SEC EDGAR / Companies House / BSE-NSE / MoneyControl)'
                  }
                  position="top"
                >
                  <span
                    tabIndex={0}
                    className={`cursor-help px-2.5 py-0.5 rounded-full border text-xs font-semibold ${
                      isPdfSourced
                        ? 'bg-violet-50 border-violet-200 text-violet-700'
                        : 'bg-neutral-50 border-neutral-200 text-neutral-600'
                    }`}
                  >
                    {isPdfSourced ? 'PDF' : 'Web'}
                  </span>
                </Tooltip>
              </div>
              <p className="text-sm text-neutral-500 mt-0.5">{company.industry || 'Industry not available'}</p>
              <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                {company.headquarters && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-neutral-100 border border-neutral-200 text-xs text-neutral-600">
                    <Globe className="h-3 w-3" /> {company.headquarters}
                  </span>
                )}
                {company.employeeCount && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-neutral-100 border border-neutral-200 text-xs text-neutral-600">
                    <Users className="h-3 w-3" /> {company.employeeCount.toLocaleString()} employees
                  </span>
                )}
                {company.dateRange && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-neutral-100 border border-neutral-200 text-xs text-neutral-600">
                    <Calendar className="h-3 w-3" />
                    {new Date(company.dateRange.from).getFullYear()} – {new Date(company.dateRange.to).getFullYear()}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Tooltip
              content={
                isPdfSourced
                  ? 'Refresh is unavailable for PDF-sourced companies — upload a newer annual report instead'
                  : 'Refresh financial data'
              }
              position="top"
            >
              <button
                onClick={handleRefresh}
                disabled={isRegenerating || isDeleting || isPdfSourced}
                aria-label="Refresh financial data"
                className="px-3 py-2 border border-primary-200 text-primary-600 rounded-lg hover:bg-primary-50 hover:border-primary-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRegenerating
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <RefreshCw className="w-4 h-4" />
                }
              </button>
            </Tooltip>
            <Tooltip content="Delete company" position="top">
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isDeleting || isRegenerating}
                aria-label="Delete company"
                className="px-3 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 hover:border-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Trash2 className="w-4 h-4" />
                }
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Quick Stats — only shown for SEC companies when v2 financial data is NOT available */}
      {hasSecReports && !latestFinancials && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card>
            <CardBody>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1">Latest Revenue</p>
                  <div className="text-2xl font-bold text-neutral-900">
                    {company.latestRevenue || 'N/A'}
                  </div>
                  <div className="text-xs text-neutral-400 mt-1">
                    {company.latestPeriod || 'No data available'}
                  </div>
                </div>
                <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center shrink-0">
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1">Net Income</p>
                  <div className="text-2xl font-bold text-neutral-900">
                    {formatCurrency(company.latestMetrics?.netIncome, uiConfig?.regionConfig)}
                  </div>
                  <div className="text-xs text-neutral-400 mt-1">
                    {company.latestMetrics?.period || company.latestPeriod || 'No data available'}
                  </div>
                </div>
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1">Gross Profit</p>
                  <div className="text-2xl font-bold text-neutral-900">
                    {formatCurrency(company.latestMetrics?.grossProfit, uiConfig?.regionConfig)}
                  </div>
                  <div className="text-xs text-neutral-400 mt-1">
                    {company.latestMetrics?.period || company.latestPeriod || 'No data available'}
                  </div>
                </div>
                <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center shrink-0">
                  <BarChart3 className="h-5 w-5 text-violet-600" />
                </div>
              </div>
            </CardBody>
          </Card>

          {hasSecReports && (
            <Card>
              <CardBody>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1">SEC Filings</p>
                    <div className="text-2xl font-bold text-neutral-900">
                      {company.reportCount}
                    </div>
                    <div className="text-xs text-neutral-400 mt-1">
                      Financial reports processed
                    </div>
                  </div>
                  <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                    <FileText className="h-5 w-5 text-amber-600" />
                  </div>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      )}

      {/* KPI Cards — shown above tabs only when the latest year has both
          statements; otherwise the BS-derived cards (EV, ROIC, Net Debt) would
          show misleading zeros while the tabs say "no data". */}
      {latestFinancials && regionConfig && latestFinancialsComplete && (
        <div className="mb-5">
          <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-2.5">Latest Financials</p>
          <MetricCardsRow latest={latestFinancials} region={regionConfig} />
        </div>
      )}

      {/* Compare with competitors — reveals the dedicated Competitors tab.
          Always visible (when the company has financial data) so it can toggle
          the tab on/off from anywhere, not just EV/Revenue. */}
      {latestFinancials && (
        <CompetitorCompareToggle
          enabled={compareWithCompetitors}
          onToggle={setCompareWithCompetitors}
          competitorsList={competitorsList}
          competitorsListLoading={competitorsQuery.isLoading || competitorsQuery.isFetching}
          competitorsListError={competitorsQuery.error as Error | null}
          financialsLoading={competitorsLoading}
          failedCompetitors={competitorFinancials.failed}
          successCount={competitorFinancials.successCount}
          onRetry={() => competitorsQuery.refetch()}
          onManage={() => setManageCompetitorsOpen(true)}
        />
      )}

      <ManageCompetitorsModal
        isOpen={manageCompetitorsOpen}
        onClose={() => setManageCompetitorsOpen(false)}
        companyName={company?.companyName ?? ''}
        peers={competitorsList}
      />

      {/* Tabs — comfortable underline tabs that wrap onto multiple rows when they
          exceed the width (shared style with the Industry Detail tab bar).

          The ref publishes this bar's MEASURED height as `--dq-sticky-top` so
          sticky content below it (the Data Quality command bar) can sit exactly
          underneath. It cannot be a constant: this nav wraps to two rows at
          laptop widths, and a hardcoded offset put the command bar behind it. */}
      <div ref={tabBarRef} className="sticky top-0 z-20 bg-white border-b border-neutral-200 mb-6">
        <nav className="flex flex-wrap gap-x-1 gap-y-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`flex flex-shrink-0 items-center gap-2 whitespace-nowrap px-4 py-3 border-b-2 font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-neutral-600 hover:text-neutral-900 hover:border-neutral-300'
              }`}
            >
              <tab.icon className={`h-5 w-5 ${activeTab === tab.id ? 'text-primary-600' : 'text-neutral-400'}`} />
              {tab.label}
              {/* Tied to the same `status !== 'clean'` rule the console itself
                  uses, so the dot never promises a finding the tab does not
                  show. Never colour alone: it is additional to the label. */}
              {tab.id === 'dataQuality' && dataQualityNeedsAttention && (
                <span
                  className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                  aria-label="has findings"
                />
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content — while a financial-data refresh is in flight we dim the
          existing figures and lay a loading card over them. Previously the only
          feedback was a spinner on the header button, so the (now-stale) numbers
          stayed fully visible below as if they were current. */}
      <div className="relative">
        <div className={isRegenerating ? 'opacity-40 pointer-events-none select-none' : ''}>
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Executive Summary + Key Insights use latest-year metrics (ROIC, EV,
              spread) — hide them when the latest year is incomplete so they don't
              present wrong analysis above the "no data" message. */}
          {latestFinancialsComplete && (
            <>
          {/* Executive Summary */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-primary-50 flex items-center justify-center">
                  <Building2 className="h-4 w-4 text-primary-600" />
                </div>
                <h2 className="text-base font-semibold text-neutral-900">Executive Summary</h2>
              </div>
            </CardHeader>
            <CardBody>
              <div className="prose prose-neutral max-w-none">
                <ReactMarkdown>
                  {company.executiveSummary
                    ?? (latestFinancials && financialData
                      ? generateExecutiveSummary(
                          company.companyName,
                          latestFinancials,
                          financialData.sector,
                          regionConfig,
                          financialData.financial_data,
                        )
                      : 'No executive summary available yet. Please check back after reports have been processed.')}
                </ReactMarkdown>
              </div>
            </CardBody>
          </Card>

          {/* Key Insights — POST /analysis/companyInsights */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-violet-50 flex items-center justify-center">
                  <TrendingUp className="h-4 w-4 text-violet-600" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-neutral-900">Key Insights</h2>
                  <p className="text-xs text-neutral-500 mt-0.5">AI-generated analysis from financial data</p>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              {insightsLoading ? (
                <LoadingCard asCard={false} message="Generating key insights…" height={120} delayMs={0} />
              ) : companyAnalysisData?.insights?.overall_summary ? (
                <div className="space-y-5">
                  <p className="text-neutral-700 text-sm leading-relaxed">
                    {companyAnalysisData.insights.overall_summary.executive_summary}
                  </p>
                  <div>
                    <h3 className="text-xs font-bold text-neutral-400 uppercase tracking-widest mb-2.5">Key Highlights</h3>
                    <ul className="space-y-2">
                      {companyAnalysisData.insights.overall_summary.key_highlights.map((h: string, i: number) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm text-neutral-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-1.5 shrink-0" />
                          {h}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {companyAnalysisData.insights.overall_summary.risk_factors?.length > 0 && (
                    <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                      <h3 className="text-xs font-bold text-red-600 uppercase tracking-widest mb-2">Risk Factors</h3>
                      <ul className="space-y-1.5">
                        {companyAnalysisData.insights.overall_summary.risk_factors.map((r: string, i: number) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-red-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 shrink-0" />
                            {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {companyAnalysisData.insights.overall_summary.recommendations && (
                    <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                      <p className="text-xs font-bold text-blue-700 uppercase tracking-widest mb-1.5">Recommendations</p>
                      <p className="text-sm text-blue-800 leading-relaxed">
                        {companyAnalysisData.insights.overall_summary.recommendations}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-10 text-neutral-400">
                  <p className="text-sm">No insights available yet.</p>
                  <p className="text-xs mt-1">Insights are generated once financial data is ingested.</p>
                </div>
              )}
            </CardBody>
          </Card>
            </>
          )}

          {/* Financial Timeline — US companies only (not enough data for India) */}
          {isUSCompany && reports.length > 0 && (
            <FinancialTimeline reports={reports} />
          )}

          {/* Financial dashboard overview (all companies) */}
          {company?.companyName && (
            <FinancialDashboardTab companyName={company.companyName} section="overview" country={companyCountry} />
          )}

          {/* Data Quality — advisory only. Renders nothing when the backend has
              no stored validation for this company (404). */}
          <DataQualityPanel
            companyId={company?.companyId}
            onOpenConsole={() => setActiveTab('dataQuality')}
          />
        </div>
      )}

      {activeTab === 'dataQuality' && (
        <DataQualityConsole companyId={company?.companyId} />
      )}

      {/* ── Financial dashboard section tabs (all companies) ────────────── */}
      {company?.companyName && activeTab === 'revenue' && (
        <FinancialDashboardTab companyName={company.companyName} section="revenue" country={companyCountry} />
      )}
      {company?.companyName && activeTab === 'ev' && (
        <FinancialDashboardTab companyName={company.companyName} section="ev" country={companyCountry} companyInsights={companyAnalysisData} />
      )}
      {company?.companyName && activeTab === 'competitors' && (
        <FinancialDashboardTab
          companyName={company.companyName}
          section="competitors"
          country={companyCountry}
          companyInsights={companyAnalysisData}
          onManageCompetitors={() => setManageCompetitorsOpen(true)}
        />
      )}
      {company?.companyName && activeTab === 'finance' && (
        <FinancialDashboardTab companyName={company.companyName} section="finance" country={companyCountry} />
      )}
      {company?.companyName && activeTab === 'sensitivity' && (
        <FinancialDashboardTab companyName={company.companyName} section="sensitivity" country={companyCountry} />
      )}

      {company?.companyName && activeTab === 'news' && (
        <NewsIntelligenceTab companyName={company.companyName} />
      )}

      {company?.companyName && activeTab === 'docInsights' && (
        <DocumentInsightsTab
          companyName={company.companyName}
          country={companyCountry.toLowerCase()}
        />
      )}

      {activeTab === 'financials' && hasSecReports && reports.length > 0 && company?.companyName && (
        <SECFilingsTab
          reports={reports}
          companyName={company.companyName}
          companyCountry={companyCountry}
        />
      )}

      {activeTab === 'businessModel' && (
        <div className="space-y-6">
          {/* Loading State */}
          {businessModelLoading && (
            <Card>
              <CardBody className="text-center py-12">
                <Loader2 className="h-16 w-16 text-primary-600 mx-auto mb-4 animate-spin" />
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                  Loading Business Model Canvas...
                </h3>
                <p className="text-neutral-600">
                  Synthesizing XBRL and LLM-extracted data
                </p>
              </CardBody>
            </Card>
          )}

          {/* Error State */}
          {!businessModelLoading && businessModelError && (
            <Card>
              <CardHeader>
                <h2 className="text-xl font-semibold text-neutral-900">
                  Business Model Canvas
                </h2>
              </CardHeader>
              <CardBody>
                <div className="text-center py-12">
                  <XCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
                  <p className="text-red-600 font-medium mb-2">Failed to Load Business Model</p>
                  <p className="text-sm text-neutral-600 max-w-md mx-auto">
                    {businessModelError}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={refreshBusinessModel}
                    className="mt-4"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Generate Business Model
                  </Button>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Success State - Display Business Model Canvas */}
          {!businessModelLoading && !businessModelError && businessModel && (
            <BusinessModelCanvas
              canvas={businessModel}
              companyName={company?.companyName}
            />
          )}

          {/* Empty State */}
          {!businessModelLoading && !businessModelError && !businessModel && (
            <Card>
              <CardHeader>
                <h2 className="text-xl font-semibold text-neutral-900">
                  Business Model Canvas
                </h2>
              </CardHeader>
              <CardBody>
                <div className="text-center py-12 text-neutral-500">
                  <Layers className="h-12 w-12 mx-auto mb-4 text-neutral-300" />
                  <p className="font-medium text-lg">No Business Model Data Available</p>
                  <p className="text-sm mt-2 max-w-md mx-auto">
                    Business model extraction requires SEC filing data. Please ensure reports have been processed for this company.
                  </p>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'research' && (
        <div className="space-y-6">
          {/* File Upload Section */}
          <Card>
            <CardHeader>
              <h2 className="text-xl font-semibold text-neutral-900">
                Custom Research
              </h2>
              <p className="text-sm text-neutral-600 mt-1">
                Upload your own custom research/insights about {company.companyName}
              </p>
            </CardHeader>
            <CardBody>
              {/* Upload Area */}
              <div className="mb-6">
                <label
                  htmlFor="research-file-upload"
                  className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-neutral-300 rounded-lg cursor-pointer bg-neutral-50 hover:bg-neutral-100 transition-colors"
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    {isUploading ? (
                      <>
                        <Loader2 className="w-10 h-10 mb-3 text-primary-600 animate-spin" />
                        <p className="text-sm text-neutral-600">Uploading...</p>
                      </>
                    ) : (
                      <>
                        <Upload className="w-10 h-10 mb-3 text-neutral-400" />
                        <p className="mb-2 text-sm text-neutral-600">
                          <span className="font-semibold">Click to upload</span> or drag and drop
                        </p>
                        <p className="text-xs text-neutral-500">
                          PDF, DOC, DOCX, TXT, or images (MAX. 50MB)
                        </p>
                      </>
                    )}
                  </div>
                  <input
                    id="research-file-upload"
                    type="file"
                    className="hidden"
                    multiple
                    accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                    onChange={(e) => {
                      const files = e.target.files;
                      if (files && files.length > 0) {
                        setIsUploading(true);
                        // Simulate upload - backend will be implemented later
                        setTimeout(() => {
                          const newFiles = Array.from(files).map((file) => ({
                            id: `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                            name: file.name,
                            size: file.size,
                            uploadedAt: new Date(),
                            type: file.type || 'application/octet-stream',
                          }));
                          setResearchFiles((prev) => [...prev, ...newFiles]);
                          setIsUploading(false);
                          e.target.value = ''; // Reset input
                        }, 1000);
                      }
                    }}
                  />
                </label>
              </div>

              {/* Uploaded Files List */}
              {researchFiles.length > 0 ? (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-neutral-700 mb-3">
                    Uploaded Files ({researchFiles.length})
                  </h3>
                  {researchFiles.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg border border-neutral-200"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                          <File className="h-5 w-5 text-primary-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-neutral-900">
                            {file.name}
                          </p>
                          <p className="text-xs text-neutral-500">
                            {(file.size / 1024).toFixed(1)} KB • Uploaded {file.uploadedAt.toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setResearchFiles((prev) =>
                            prev.filter((f) => f.id !== file.id)
                          );
                        }}
                        className="text-neutral-500 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-neutral-500">
                  <FileUp className="h-12 w-12 mx-auto mb-4 text-neutral-300" />
                  <p>No research files uploaded yet</p>
                  <p className="text-sm mt-2">
                    Upload documents, reports, or notes to enhance your analysis of {company.companyName}
                  </p>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      )}

      {activeTab === 'reports' && company?.cik && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <h2 className="text-xl font-semibold text-neutral-900">
                Quarterly Financial Reports
              </h2>
            </CardHeader>
            <CardBody>
              <QuarterlyFilingsTable
                companyId={company.companyId}
                companyName={company.companyName}
                cik={company.cik}
                processedReports={reports}
                onRefreshNeeded={() => {
                  // Refresh company data after processing (without reloading page)
                  fetchCompanyData();
                }}
              />
            </CardBody>
          </Card>
        </div>
      )}

      {activeTab === 'filings' && isNonUSCompany && company?.companyId && (
        // Kept consistent with the analysis gate: when the latest year is missing
        // a statement we don't present a fully-populated filings view alongside a
        // "no data" verdict elsewhere on the page. (The underlying filings do
        // carry P&L figures — only the Balance Sheet is absent — so if you'd
        // rather surface them here, drop this branch.)
        latestFinancials && !latestFinancialsComplete ? (
          <FinancialNoData
            state="incomplete"
            missing={
              !hasProfitLoss([latestFinancials]) && !hasBalanceSheet([latestFinancials])
                ? 'both'
                : !hasBalanceSheet([latestFinancials])
                ? 'balance-sheet'
                : 'profit-loss'
            }
            companyName={company.companyName}
          />
        ) : (
          <CompanyFilingsTab
            companyId={company.companyId}
            companyCountry={companyCountry as 'INDIA' | 'UK'}
            companyName={company.companyName}
            regionConfig={regionConfig ?? null}
          />
        )
      )}
        </div>

        {isRegenerating && (
          <div className="absolute inset-0 z-30 flex items-start justify-center pt-24">
            <div className="flex items-center gap-3 px-5 py-3.5 bg-white border border-primary-200 rounded-xl shadow-lg">
              <Loader2 className="w-5 h-5 text-primary-600 animate-spin shrink-0" />
              <span className="text-sm font-medium text-neutral-700">
                Refreshing financial data for {company.companyName}…
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Report Details Modal */}
      <ReportDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        companyId={companyId || ''}
        reportId={selectedReportId || ''}
      />

      {/* Delete confirmation */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete Company"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <button
              onClick={handleConfirmDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium text-sm transition-colors"
            >
              Delete
            </button>
          </>
        }
      >
        <div className="flex gap-4">
          <div className="shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-neutral-900 mb-1">
              Delete &ldquo;{company?.companyName}&rdquo;?
            </p>
            <p className="text-sm text-neutral-500">
              This will permanently remove the company and all its financial reports. This action cannot be undone.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
}
